import { useState, useEffect, useRef } from 'react';
import { Text, Spinner } from '@shopify/polaris';

interface AIChatWidgetProps {
  shop: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function AIChatWidget({ shop }: AIChatWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  // Default to enabled - will be updated when settings load
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiSettings, setAiSettings] = useState({
    ai_section_title: 'AI Assistant',
    ai_section_subtitle: 'Ask me anything about orders, shipping, and returns!',
    ai_bg_color: '#008060',
    ai_text_color: '#ffffff',
    ai_welcome_heading: 'Hi! I\'m your AI assistant',
    ai_welcome_message: 'I can help you with questions about orders, shipping, returns, and more.',
    ai_suggested_questions: ['How long does shipping take?', 'What is your return policy?', 'Do you ship internationally?'],
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Check if AI is enabled and load settings
  useEffect(() => {
    const checkAIStatus = async () => {
      try {
        const apiUrl = (window as any).__WIDGET_API_URL__ || window.location.origin;
        const settingsUrl = `${apiUrl}/api/admin/settings/public?shop=${encodeURIComponent(shop)}`;
        console.log('[AIChatWidget] Loading settings from:', settingsUrl);
        
        // Use XMLHttpRequest since fetch has issues in iframe context
        const data = await new Promise<any>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', settingsUrl, true);
          xhr.timeout = 10000; // 10 second timeout
          xhr.setRequestHeader('Accept', 'application/json');
          
          const timeout = setTimeout(() => {
            console.error('[AIChatWidget] Settings request timed out after 10 seconds');
            xhr.abort();
            reject(new Error('Request timeout'));
          }, 10000);
          
          xhr.onload = function() {
            clearTimeout(timeout);
            if (xhr.status === 200) {
              try {
                const responseData = JSON.parse(xhr.responseText);
                console.log('[AIChatWidget] Settings loaded successfully:', responseData);
                resolve(responseData);
              } catch (e) {
                console.error('[AIChatWidget] Failed to parse settings response:', e);
                reject(new Error('Failed to parse response'));
              }
            } else {
              console.error('[AIChatWidget] Failed to load settings:', xhr.status, xhr.statusText);
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          };
          
          xhr.onerror = function() {
            clearTimeout(timeout);
            console.error('[AIChatWidget] Network error loading settings');
            reject(new Error('Network error'));
          };
          
          xhr.ontimeout = function() {
            clearTimeout(timeout);
            console.error('[AIChatWidget] Settings request timed out');
            reject(new Error('Request timeout'));
          };
          
          xhr.send();
        });
        
        const enabled = data.ai_enabled === 1 || data.ai_enabled === true;
        console.log('[AIChatWidget] AI enabled:', enabled);
        setAiEnabled(enabled);
        
        let suggestedQuestions = ['How long does shipping take?', 'What is your return policy?', 'Do you ship internationally?'];
        if (data.ai_suggested_questions) {
          try {
            suggestedQuestions = typeof data.ai_suggested_questions === 'string' 
              ? JSON.parse(data.ai_suggested_questions)
              : data.ai_suggested_questions;
          } catch (e) {
            console.error('[AIChatWidget] Failed to parse suggested questions:', e);
          }
        }
        
        setAiSettings({
          ai_section_title: data.ai_section_title || 'AI Assistant',
          ai_section_subtitle: data.ai_section_subtitle || 'Ask me anything about orders, shipping, and returns!',
          ai_bg_color: data.ai_bg_color || '#008060',
          ai_text_color: data.ai_text_color || '#ffffff',
          ai_welcome_heading: data.ai_welcome_heading || 'Hi! I\'m your AI assistant',
          ai_welcome_message: data.ai_welcome_message || 'I can help you with questions about orders, shipping, returns, and more.',
          ai_suggested_questions: suggestedQuestions.filter((q: string) => q && q.trim()),
        });
        
        console.log('[AIChatWidget] AI settings updated:', {
          title: data.ai_section_title,
          subtitle: data.ai_section_subtitle,
          bgColor: data.ai_bg_color,
          textColor: data.ai_text_color,
        });
      } catch (error: any) {
        console.error('[AIChatWidget] Failed to check AI status:', error);
        console.warn('[AIChatWidget] Defaulting to enabled due to error');
        // Default to enabled if there's an error loading settings
        setAiEnabled(true);
      }
    };

    if (shop) {
      checkAIStatus();
    } else {
      // If no shop, default to enabled
      console.warn('[AIChatWidget] No shop provided, defaulting to enabled');
      setAiEnabled(true);
    }
  }, [shop]);

  // Scroll to bottom when messages change - only within chat container
  useEffect(() => {
    if (messagesContainerRef.current) {
      // Scroll only within the chat container, not the page
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const apiUrl = (window as any).__WIDGET_API_URL__ || window.location.origin;
      console.log('[AIChatWidget] Sending message:', { shop, message: userMessage, apiUrl });
      
      // Use XMLHttpRequest since fetch is timing out
      const chatUrl = `${apiUrl}/api/ai/chat`;
      console.log('[AIChatWidget] Using XMLHttpRequest for AI chat (fetch has issues)');
      
      const response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', chatUrl, true);
        xhr.timeout = 30000;
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        
        const timeout = setTimeout(() => {
          console.error('[AIChatWidget] AI chat request timed out after 30 seconds');
          xhr.abort();
          reject(new Error('Request timeout'));
        }, 30000);
        
        xhr.onload = function() {
          clearTimeout(timeout);
          const response = new Response(xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
          });
          resolve(response);
        };
        
        xhr.onerror = function() {
          clearTimeout(timeout);
          reject(new Error('Network error'));
        };
        
        xhr.ontimeout = function() {
          clearTimeout(timeout);
          reject(new Error('Request timeout'));
        };
        
        xhr.send(JSON.stringify({
          shop,
          message: userMessage,
          conversationHistory: messages,
        }));
      });

      console.log('[AIChatWidget] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AIChatWidget] API error:', response.status, errorText);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Error (${response.status}): ${errorText || response.statusText || 'Failed to get response'}` 
        }]);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('[AIChatWidget] Response data:', data);

      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else if (data.error) {
        console.error('[AIChatWidget] Error in response:', data.error);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Error: ${data.error}` 
        }]);
      } else {
        console.warn('[AIChatWidget] No response or error in data:', data);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please try again.' 
        }]);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('[AIChatWidget] AI chat request was aborted (timeout)');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Request timed out. Please check your connection and try again.' 
        }]);
      } else {
        console.error('[AIChatWidget] Network/parsing error:', error);
        console.error('[AIChatWidget] Error name:', error.name);
        console.error('[AIChatWidget] Error message:', error.message);
        console.error('[AIChatWidget] Error stack:', error.stack);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Network error: ${error.message || 'Unable to connect. Please check your connection and try again.'}` 
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!aiEnabled) {
    return null; // Don't show chat if AI is disabled
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      marginTop: '32px',
      marginBottom: '80px',
      border: '2px solid #e1e3e5',
      borderRadius: '8px',
      backgroundColor: '#fafbfb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '500px',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: aiSettings.ai_bg_color,
        color: aiSettings.ai_text_color,
        borderRadius: '6px 6px 0 0',
        borderBottom: `1px solid ${aiSettings.ai_bg_color}`,
      }}>
        <Text as="h3" variant="headingLg" fontWeight="bold" tone="inherit">
          {aiSettings.ai_section_title}
        </Text>
        <Text as="p" variant="bodySm" tone="inherit">
          {aiSettings.ai_section_subtitle}
        </Text>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        style={{
          flex: 1,
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          backgroundColor: 'white',
          minHeight: '300px',
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {messages.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: '#6d7175',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👋</div>
            <Text as="p" variant="headingMd" fontWeight="semibold">
              {aiSettings.ai_welcome_heading}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {aiSettings.ai_welcome_message}
            </Text>
            {aiSettings.ai_suggested_questions.length > 0 && (
              <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <Text as="p" variant="bodySm" tone="subdued">Try asking:</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                  {aiSettings.ai_suggested_questions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInputValue(q)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#f6f6f7',
                        border: '1px solid #e1e3e5',
                        borderRadius: '16px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        color: '#202223',
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '75%',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: msg.role === 'user' ? '#008060' : '#f6f6f7',
                color: msg.role === 'user' ? 'white' : '#202223',
                wordBreak: 'break-word',
                lineHeight: '1.5',
              }}
            >
              <Text as="p" variant="bodyMd" tone={msg.role === 'user' ? 'inherit' : undefined}>
                {msg.content}
              </Text>
            </div>
            <Text as="p" variant="bodySm" tone="subdued" alignment={msg.role === 'user' ? 'end' : 'start'}>
              {msg.role === 'user' ? 'You' : 'AI Assistant'}
            </Text>
          </div>
        ))}

        {loading && (
          <div style={{ alignSelf: 'flex-start', maxWidth: '75%' }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#f6f6f7',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Spinner size="small" />
              <Text as="p" variant="bodyMd">
                Thinking...
              </Text>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e1e3e5',
        backgroundColor: 'white',
        borderRadius: '0 0 6px 6px',
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type your message here..."
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #e1e3e5',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
            <button
              onClick={sendMessage}
              disabled={loading || !inputValue.trim()}
              style={{
                padding: '12px 24px',
                backgroundColor: aiSettings.ai_bg_color,
                color: aiSettings.ai_text_color,
                border: 'none',
                borderRadius: '8px',
                cursor: loading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !inputValue.trim() ? 0.5 : 1,
                fontWeight: '600',
                fontSize: '14px',
                fontFamily: 'inherit',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => !loading && inputValue.trim() && (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={(e) => e.currentTarget.style.opacity = loading || !inputValue.trim() ? '0.5' : '1'}
            >
              Send
            </button>
        </div>
      </div>
    </div>
  );
}
